/**
 * Debug logger utility for structured logging
 * Provides consistent logging across the library
 */
export class DebugLogger {
	constructor(private enabled: boolean = false) {}

	/**
	 * Log OAuth-related debug information
	 */
	oauth(operation: string, url: string, body?: string, response?: unknown): void {
		if (!this.enabled) return;

		console.log(`🔍 Debug - OAuth ${operation}:`);
		console.log('URL:', url);
		if (body) {
			console.log('Body:', body);
		}
		if (response) {
			console.log('Response:', response);
		}
	}

	/**
	 * Log API request debug information
	 */
	apiRequest(url: string, options: RequestInit): void {
		if (!this.enabled) return;

		console.log('🔍 Debug - API Request:');
		console.log('URL:', url);
		console.log('Options:', this.sanitizeOptions(options));
	}

	/**
	 * Log API error debug information
	 */
	apiError(
		status: number,
		statusText: string,
		headers: Record<string, string>,
		body: unknown,
		endpoint: string
	): void {
		if (!this.enabled) return;

		console.log('🔍 Debug - API Error Response:');
		console.log('Status:', status, statusText);
		console.log('Headers:', headers);
		console.log('Body:', body);
		console.log('Endpoint:', endpoint);
	}

	/**
	 * Log token refresh attempts
	 */
	tokenRefresh(message: string, error?: Error): void {
		if (!this.enabled) return;

		console.log('🔄 Token refresh:', message);
		if (error) {
			console.log('Error:', error.message);
		}
	}

	/**
	 * Log general information
	 */
	info(message: string, data?: unknown): void {
		if (!this.enabled) return;

		console.log('ℹ️', message);
		if (data) {
			console.log(data);
		}
	}

	/**
	 * Log warnings
	 */
	warn(message: string, data?: unknown): void {
		if (!this.enabled) return;

		console.warn('⚠️', message);
		if (data) {
			console.warn(data);
		}
	}

	/**
	 * Sanitize request options for logging (remove sensitive data)
	 */
	private sanitizeOptions(options: RequestInit): Partial<RequestInit> {
		const sanitized = { ...options };
		
		// Remove or mask sensitive headers
		if (sanitized.headers) {
			const headers = { ...sanitized.headers } as Record<string, string>;
			if (headers.Authorization) {
				headers.Authorization = 'Bearer ***';
			}
			sanitized.headers = headers;
		}

		return sanitized;
	}
}
